import React, { useState, useEffect, useImperativeHandle, useRef, useMemo } from 'react'
import { Popup } from 'antd-mobile'
import styles from './AgreementModal.module.scss'
import CloseIcon from '@/assets/Close.svg'
import PdfViewer from '@/components/pdfViewer/PdfViewer'
import usePrivateOssUrl from '@/hooks/usePrivateOssUrl'
import type { ContractTempVO } from '@/subCodeFruit/apis.type'
import type { marketing_loan_disbursement_protoco_response } from '@/subCodeFruit/apis.type'

export interface AgreementModalRef {
  show: (isSubmit?: boolean) => void
  hide: () => void
}

export interface ContractTempWithContent extends ContractTempVO {
  content?: string
  agreementType?: number
}

export interface AgreementModalProps {
  agreementType?: number | null
  isSubmit?: boolean
  contractTemplate?: ContractTempVO
  contractTemplateArr?: marketing_loan_disbursement_protoco_response
  onSuccess?: (isSubmit?: boolean) => void
  onClose?: () => void
  preventInitialExit?: boolean
}

const AgreementModal = React.forwardRef<AgreementModalRef, AgreementModalProps>(
  ({ contractTemplate, contractTemplateArr, onSuccess, onClose, preventInitialExit = false }, ref) => {
    const [visible, setVisible] = useState(false)
    const [timeDown, setTimeDown] = useState(0)
    const isSubmitRef = useRef(false)
    const [blockFirstHidden, setBlockFirstHidden] = useState(true)

    // 多协议模式
    const isMultiMode = !!contractTemplateArr?.length
    const [activeIndex, setActiveIndex] = useState(0)
    const currentContract = isMultiMode ? contractTemplateArr?.[activeIndex] : null

    const src = contractTemplate?.contractTemplateUrl || ''
    const targetUrl = usePrivateOssUrl(src)

    // 多协议模式下的 URL
    const multiSrc = currentContract?.url || ''
    const multiTargetUrl = usePrivateOssUrl(multiSrc)

    const show = (isSubmit: boolean = false) => {
      setVisible(true)
      isSubmitRef.current = isSubmit
      if (isSubmit) {
        setTimeDown(10)
      }
    }

    const hide = () => {
      setTimeDown(0)

      if (!preventInitialExit) {
        setVisible(false)
        onClose?.()
        return
      }

      if (!blockFirstHidden) {
        setVisible(false)
        setBlockFirstHidden(true)
      }
      if (visible && blockFirstHidden) {
        onClose?.()
        setBlockFirstHidden(false)
      }
    }

    useEffect(() => {
      if (timeDown > 0) {
        const timer = setTimeout(() => {
          setTimeDown((prev) => prev - 1)
        }, 1000)
        return () => clearTimeout(timer)
      }
    }, [timeDown])

    useImperativeHandle(ref, () => ({
      show,
      hide,
    }))

    const disabled = timeDown > 0

    const handleSubmit = async () => {
      if (disabled) {
        return
      }

      onSuccess?.(isSubmitRef.current)
    }
    return (
      <Popup
        visible={visible}
        onMaskClick={hide}
        bodyStyle={{
          height: '100%',
          backgroundColor: 'transparent',
        }}
      >
        <div className={styles.modalMask} onClick={hide}>
          <div className={styles.modalWrap} onClick={(e) => e.stopPropagation()}>
            <img src={CloseIcon} alt="" className={styles.closeIcon} onClick={hide} />
            <div className={styles.modalTitle}>
              {isMultiMode ? currentContract?.protocolName : contractTemplate?.contractTemplateName}
            </div>
            <div className={styles.modalTip}>
              请仔细阅读并同意
              {isMultiMode ? (
                contractTemplateArr?.map((item, index) => (
                  <span
                    key={item.protocolName}
                    onClick={() => setActiveIndex(index)}
                    style={{
                      cursor: 'pointer',
                      fontWeight: activeIndex === index ? 'bold' : 'normal',
                      textDecoration: activeIndex === index ? 'underline' : 'none',
                    }}
                  >
                    《{item.protocolName}》
                  </span>
                ))
              ) : (
                <span>《{contractTemplate?.contractTemplateName}》</span>
              )}
            </div>
            <div className={styles.modalContent}>
              {isMultiMode ? (
                currentContract?.agreementType === 0 ? (
                  <div
                    className={styles.textContent}
                    dangerouslySetInnerHTML={{ __html: currentContract?.content || '' }}
                  />
                ) : (
                  <PdfViewer file={multiTargetUrl} className={styles.pdfContainer} />
                )
              ) : (
                <PdfViewer file={targetUrl} className={styles.pdfContainer} />
              )}
            </div>
            <div className={`${styles.modalBtn} ${disabled ? styles.modalBtnDisabled : ''}`} onClick={handleSubmit}>
              我同意并签署授信相关协议{timeDown > 0 ? ` (${timeDown}s)` : ''}
            </div>
          </div>
        </div>
      </Popup>
    )
  },
)

export default AgreementModal
